/**
 * UX2 — parallel workspace data (family + compare + submission history).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import quotationService, { type Quotation } from '@/services/quotation.service';
import type { QuotationRevisionCompareResult } from '@/components/crm/QuotationRevisionComparePanel';

export function useQuotationWorkspaceData(quotation: Quotation | undefined) {
  const id = quotation?.id;

  const familyQuery = useQuery({
    queryKey: ['quotation-revision-family', id],
    queryFn: () => quotationService.listRevisionFamily(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const family = familyQuery.data || (quotation ? [quotation] : []);
  const sortedFamily = useMemo(
    () => [...family].sort((a, b) => Number(a.revision_number) - Number(b.revision_number)),
    [family]
  );

  const defaultCompareWithId =
    quotation?.parent_quotation_id ||
    sortedFamily.find((q) => q.revision_number === Number(quotation?.revision_number) - 1)?.id ||
    sortedFamily.find((q) => q.id !== quotation?.id)?.id;

  const compareQuery = useQuery({
    queryKey: ['quotation-revision-compare', id, defaultCompareWithId],
    queryFn: () => quotationService.compareRevisions(id!, defaultCompareWithId!),
    enabled: !!id && !!defaultCompareWithId && defaultCompareWithId !== id,
    staleTime: 30_000,
  });

  const submissionQuery = useQuery({
    queryKey: ['quotation-submission-history', id],
    queryFn: () => quotationService.listSubmissionHistory(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const currentOffer = sortedFamily.find((r) => r.is_current_submitted);

  return {
    family: sortedFamily,
    familyLoading: familyQuery.isLoading,
    familyError: familyQuery.error,
    comparison: compareQuery.data as QuotationRevisionCompareResult | undefined,
    compareLoading: compareQuery.isLoading,
    compareError: compareQuery.error,
    submissionHistory: submissionQuery.data || [],
    submissionLoading: submissionQuery.isLoading,
    submissionError: submissionQuery.error,
    currentOffer,
    defaultCompareWithId,
    refetchFamily: familyQuery.refetch,
    refetchCompare: compareQuery.refetch,
    refetchSubmission: submissionQuery.refetch,
  };
}
